import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Star, Sparkles, Crown, Quote, Filter, Users, ArrowRight } from "lucide-react";
import QRCode from "../components/QRCode";

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_ICONS = { ambassador: Star, mentor_partner: Sparkles, master_partner: Crown };
const TIER_COLORS = { ambassador: "#a48cff", mentor_partner: "#D4AF37", master_partner: "#F2D6D6" };

const TIER_FILTERS = [
  { key: null, label: "Alle Stufen" },
  { key: "master_partner", label: "Master Partner" },
  { key: "mentor_partner", label: "Mentor Partner" },
  { key: "ambassador", label: "Ambassador" },
];

const SORTS = [
  { key: "helped", label: "Meiste Begleitete" },
  { key: "tier", label: "Stufe" },
];

export default function PartnerWall() {
  const [items, setItems] = useState(null);
  const [tier, setTier] = useState(null);
  const [sort, setSort] = useState("helped");

  useEffect(() => {
    const params = new URLSearchParams();
    if (tier) params.set("tier", tier);
    params.set("sort", sort);
    axios.get(`${API}/api/partners/wall?${params}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [tier, sort]);

  const counts = useMemo(() => {
    if (!items) return { total: 0, master: 0, mentor: 0, amb: 0, helped: 0 };
    return {
      total: items.length,
      master: items.filter((x) => x.tier_key === "master_partner").length,
      mentor: items.filter((x) => x.tier_key === "mentor_partner").length,
      amb: items.filter((x) => x.tier_key === "ambassador").length,
      helped: items.reduce((s, x) => s + (x.helped_count || 0), 0),
    };
  }, [items]);

  return (
    <div className="min-h-screen bg-[#081328] text-[#F7F6F2] pt-28" data-testid="partner-wall-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-30"
             style={{ background: "radial-gradient(ellipse at 20% 20%, #4B2E83 0%, transparent 50%), radial-gradient(ellipse at 80% 80%, #D4AF37 0%, transparent 55%)" }} />
        <div className="relative container-sat max-w-6xl py-16">
          <div className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-5">Die SAT Partner</div>
          <h1 className="serif text-5xl md:text-7xl font-light tracking-tight leading-[1.05]">
            Eine wachsende<br/>
            <span className="italic" style={{ color: "#D4AF37" }}>Bewegung.</span>
          </h1>
          <p className="text-white/65 text-base md:text-xl mt-6 max-w-2xl leading-relaxed">
            Jeder Stern hier ist ein Mensch, der die Mission der SAT Akademie weiterträgt. Hinter jedem Namen steht eine Geschichte von Mut, Hingabe und gelebter Transformation.
          </p>

          {/* KPI strip */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="wall-kpis">
            <Kpi label="Aktive Partner" value={counts.total} accent="#D4AF37" />
            <Kpi label="Master Partner" value={counts.master} accent={TIER_COLORS.master_partner} />
            <Kpi label="Mentor Partner" value={counts.mentor} accent={TIER_COLORS.mentor_partner} />
            <Kpi label="Begleitete Menschen" value={counts.helped} accent="#48d597" />
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-y border-white/5 bg-white/[0.02]">
        <div className="container-sat max-w-6xl py-5 flex flex-wrap items-center gap-4">
          <div className="inline-flex items-center gap-2 text-xs text-white/55"><Filter size={14} /> Filter:</div>
          <div className="flex flex-wrap gap-1.5" data-testid="wall-tier-filters">
            {TIER_FILTERS.map(({ key, label }) => (
              <button key={key || "all"} onClick={() => setTier(key)}
                className={`px-3 py-1.5 text-xs rounded-full border transition ${
                  tier === key ? "bg-[#D4AF37] text-[#081328] border-[#D4AF37]" : "border-white/15 text-white/65 hover:border-[#D4AF37]/50"
                }`}
                data-testid={`tier-filter-${key || "all"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="hidden md:block w-px h-5 bg-white/10" />
          <div className="inline-flex items-center gap-2 text-xs text-white/55">Sortieren:</div>
          <div className="flex gap-1.5" data-testid="wall-sort">
            {SORTS.map((s) => (
              <button key={s.key} onClick={() => setSort(s.key)}
                className={`px-3 py-1.5 text-xs rounded-full border transition ${
                  sort === s.key ? "bg-[#a48cff] text-[#081328] border-[#a48cff]" : "border-white/15 text-white/65 hover:border-[#a48cff]/50"
                }`}
                data-testid={`sort-${s.key}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="container-sat max-w-6xl py-16">
        {items === null && <div className="text-center text-white/45 py-16">Lade…</div>}
        {items?.length === 0 && (
          <div className="text-center py-20 border border-dashed border-white/10 rounded-2xl" data-testid="wall-empty">
            <Users size={42} className="mx-auto text-white/30 mb-4" />
            <h3 className="serif text-2xl text-white/75">Noch ist die Wand still.</h3>
            <p className="text-white/45 mt-2 text-sm max-w-md mx-auto">Sobald sich erste SAT Partner für das Spotlight angemeldet haben, erscheinen sie hier.</p>
            <Link to="/referrals" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-[#081328] font-semibold rounded-full text-sm hover:bg-[#e3c252] transition" data-testid="wall-empty-cta">
              Werde der erste <ArrowRight size={14} />
            </Link>
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="wall-grid">
          {items?.map((p, idx) => <Card key={p.user_id} p={p} delay={idx * 60} />)}
        </div>
      </section>

      {/* CTA bottom */}
      {items?.length > 0 && (
        <section className="border-t border-white/5 py-20">
          <div className="max-w-4xl mx-auto px-6 grid md:grid-cols-[1fr,auto] items-center gap-10">
            <div className="text-center md:text-left">
              <h3 className="serif text-3xl md:text-4xl font-light leading-tight">Möchtest du Teil dieser Wand werden?</h3>
              <p className="text-white/55 mt-4">Werde SAT Partner, aktiviere dein Spotlight — und werde sichtbar in der Bewegung.</p>
              <Link to="/referrals" className="mt-8 inline-flex items-center gap-2 px-7 py-3 bg-[#D4AF37] text-[#081328] font-semibold rounded-full hover:bg-[#e3c252] transition" data-testid="wall-bottom-cta">
                SAT Partner werden <ArrowRight size={14} />
              </Link>
            </div>
            <QRCode value={typeof window !== "undefined" ? window.location.href : "https://sat-akademie.com/partner-wall"} size={6} label="Diese Wand teilen" />
          </div>
        </section>
      )}

      <style>{`
        .pw-card-anim { animation: pwIn 0.6s ease-out backwards; }
        @keyframes pwIn { from { opacity:0; transform: translateY(14px); } to { opacity:1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

const Kpi = ({ label, value, accent }) => (
  <div className="border border-white/10 rounded-xl p-4 bg-white/[0.02]">
    <div className="text-[10px] uppercase tracking-[0.25em] text-white/45">{label}</div>
    <div className="serif text-3xl font-light mt-1" style={{ color: accent }}>{value}</div>
  </div>
);

const Card = ({ p, delay = 0 }) => {
  const Icon = TIER_ICONS[p.tier_key] || Star;
  const color = TIER_COLORS[p.tier_key] || "#D4AF37";
  return (
    <div className="pw-card-anim group relative rounded-2xl p-6 border border-white/10 bg-white/[0.02] hover:border-opacity-100 transition-all duration-500 overflow-hidden"
         style={{ animationDelay: `${delay}ms`, borderColor: `${color}30` }}
         data-testid={`wall-card-${p.user_id}`}>
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
           style={{ background: `radial-gradient(circle at top right, ${color}25 0%, transparent 60%)` }} />

      <div className="relative">
        <div className="flex items-start justify-between mb-4">
          {p.avatar_url ? (
            <img src={p.avatar_url} alt={p.name} className="w-14 h-14 rounded-full object-cover border-2" style={{ borderColor: color }} />
          ) : (
            <div className="w-14 h-14 rounded-full flex items-center justify-center border-2" style={{ borderColor: color, background: `${color}15` }}>
              <Icon size={22} style={{ color }} />
            </div>
          )}
          <span className="text-[9px] uppercase tracking-[0.2em] px-2 py-1 rounded-full whitespace-nowrap"
                style={{ color, border: `1px solid ${color}50` }}>{p.tier_label}</span>
        </div>

        <h3 className="serif text-2xl font-light" style={{ color }}>{p.name}</h3>

        {p.helped_count > 0 && (
          <div className="text-white/55 text-xs mt-1">
            {p.helped_count} {p.helped_count === 1 ? "Mensch begleitet" : "Menschen begleitet"}
          </div>
        )}

        {p.quote ? (
          <div className="mt-5 relative pl-6">
            <Quote size={14} className="absolute left-0 top-0.5" style={{ color }} />
            <p className="text-white/75 italic text-sm leading-relaxed line-clamp-4">{p.quote}</p>
          </div>
        ) : (
          <div className="mt-5 text-white/30 text-xs italic">Noch ohne Zitat — bald folgt mehr.</div>
        )}
      </div>
    </div>
  );
};
